import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, Download, AlertCircle, CheckCircle2, Info } from "lucide-react";
import { CREW_ROLES } from "@produce/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { GearBadge } from "@/components/team/GearBadge";
import {
  BULK_COLUMNS,
  BULK_CSV_TEMPLATE,
  emptyBulkRow,
  isBulkRowEmpty,
  emailKey,
  pasteIntoGrid,
  validateRows,
  validatedRowToForm,
  type BulkColumn,
  type BulkRow,
  type DuplicateResolution,
  type TeamMember,
  type ValidatedRow,
} from "@/lib/teamMembers";
import type { BulkSaveRow } from "@/hooks/useTeamMembers";

const ROLE_DATALIST_ID = "bulk-role-options";

const COLUMN_LABELS: Record<BulkColumn, string> = {
  name: "Name *",
  email: "Email *",
  phone: "Phone",
  role: "Role",
  gear: "Gear",
};

const COLUMN_PLACEHOLDERS: Record<BulkColumn, string> = {
  name: "Full name",
  email: "email@example.com",
  phone: "+31 6...",
  role: "e.g. video general",
  gear: "RED, FX6, MacBook",
};

const INITIAL_ROWS = 5;
const makeInitialRows = () => Array.from({ length: INITIAL_ROWS }, emptyBulkRow);

interface BulkAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingMembers: TeamMember[];
  saving: boolean;
  onSubmit: (rows: BulkSaveRow[]) => void;
}

/**
 * Paste-grid for adding many crew members at once. Rows = members,
 * columns = Name/Email/Phone/Role/Gear; a tab/newline paste (from Excel
 * or Sheets) spreads across the grid. Live per-row validation; duplicate
 * emails are resolved per row (skip or update).
 */
export const BulkAddDialog = ({
  open,
  onOpenChange,
  existingMembers,
  saving,
  onSubmit,
}: BulkAddDialogProps) => {
  const [rows, setRows] = useState<BulkRow[]>(makeInitialRows);
  // Keyed by the duplicate's email (its stable identity), NOT the row
  // index — so a skip/update choice can't drift onto another row when
  // rows are added or removed above it.
  const [resolutions, setResolutions] = useState<Record<string, DuplicateResolution>>({});
  // The row currently being edited. Its validation messages stay hidden
  // until focus leaves the row, so you don't get "Email is missing" while
  // you're still typing the name.
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const cellRefs = useRef(new Map<string, HTMLInputElement>());

  useEffect(() => {
    if (open) {
      setRows(makeInitialRows());
      setResolutions({});
      setActiveRow(null);
    }
  }, [open]);

  // Always keep exactly one blank row at the bottom so there's always an
  // empty row to type into without clicking "Add row".
  useEffect(() => {
    const last = rows[rows.length - 1];
    if (last && !isBulkRowEmpty(last)) {
      setRows((prev) => [...prev, emptyBulkRow()]);
    }
  }, [rows]);

  const existingEmails = useMemo(
    () => new Set(existingMembers.map((m) => emailKey(m.email))),
    [existingMembers],
  );

  const validated = useMemo(() => validateRows(rows, { existingEmails }), [rows, existingEmails]);

  // Map validated rows back to their grid row index for inline status.
  const statusByRow = useMemo(() => {
    const map = new Map<number, ValidatedRow>();
    validated.forEach((v) => map.set(v.index, v));
    return map;
  }, [validated]);

  const setCell = (r: number, col: BulkColumn, value: string) => {
    setRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      next[r][col] = value;
      return next;
    });
  };

  const handlePaste = (r: number, col: BulkColumn, e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text/plain");
    if (!text.includes("\t") && !text.includes("\n")) return; // single cell → default
    e.preventDefault();
    setRows((prev) => pasteIntoGrid(prev, r, col, text));
  };

  const focusCell = (r: number, colIndex: number) => {
    const col = BULK_COLUMNS[colIndex];
    if (!col) return;
    cellRefs.current.get(`${r}-${col}`)?.focus();
  };

  // Spreadsheet-style keyboard navigation: Up/Down move between rows,
  // Enter moves down, Left/Right jump columns only at the text boundary
  // (so arrows still move the caret within a field).
  const handleKeyDown = (r: number, col: BulkColumn, e: React.KeyboardEvent<HTMLInputElement>) => {
    const colIndex = BULK_COLUMNS.indexOf(col);
    const input = e.currentTarget;
    if (e.key === "Enter" || e.key === "ArrowDown") {
      if (r < rows.length - 1) {
        e.preventDefault();
        focusCell(r + 1, colIndex);
      }
    } else if (e.key === "ArrowUp") {
      if (r > 0) {
        e.preventDefault();
        focusCell(r - 1, colIndex);
      }
    } else if (e.key === "ArrowLeft") {
      if (input.selectionStart === 0 && input.selectionEnd === 0 && colIndex > 0) {
        e.preventDefault();
        focusCell(r, colIndex - 1);
      }
    } else if (e.key === "ArrowRight") {
      const end = input.value.length;
      if (
        input.selectionStart === end &&
        input.selectionEnd === end &&
        colIndex < BULK_COLUMNS.length - 1
      ) {
        e.preventDefault();
        focusCell(r, colIndex + 1);
      }
    }
  };

  const handleBlur = () => {
    // After focus settles, reveal the row's messages once focus has left
    // the grid entirely (moving between cells keeps activeRow on the new
    // cell's row via its onFocus).
    requestAnimationFrame(() => {
      const active = document.activeElement as HTMLElement | null;
      if (!active || !active.closest("[data-bulk-grid]")) setActiveRow(null);
    });
  };

  const addRow = () => setRows((prev) => [...prev, emptyBulkRow()]);
  const removeRow = (r: number) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== r) : prev));
  const clearAll = () => {
    setRows(makeInitialRows());
    setResolutions({});
  };

  const downloadTemplate = () => {
    const blob = new Blob([BULK_CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "team-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Tally what will happen on save. A row that's still being edited and
  // not yet complete isn't flagged as an error — it's just in progress.
  const summary = useMemo(() => {
    let insert = 0;
    let update = 0;
    let skip = 0;
    let error = 0;
    for (const v of validated) {
      if (v.severity === "error") {
        if (v.index !== activeRow) error += 1;
      } else if (v.duplicateOf) {
        if ((resolutions[v.duplicateOf] ?? "skip") === "update") update += 1;
        else skip += 1;
      } else {
        insert += 1;
      }
    }
    return { insert, update, skip, error };
  }, [validated, resolutions, activeRow]);

  const buildSaveRows = (): BulkSaveRow[] => {
    const out: BulkSaveRow[] = [];
    for (const v of validated) {
      if (v.severity === "error") continue;
      if (v.duplicateOf) {
        if ((resolutions[v.duplicateOf] ?? "skip") !== "update") continue;
        out.push({ form: validatedRowToForm(v), mode: "update" });
      } else {
        out.push({ form: validatedRowToForm(v), mode: "insert" });
      }
    }
    return out;
  };

  const canSave = summary.insert + summary.update > 0 && summary.error === 0 && !saving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Add multiple members</DialogTitle>
          <DialogDescription>
            Paste rows straight from Excel or Sheets (columns: Name, Email, Phone, Role, Gear). Each
            row is a crew member. Gear: comma-separated, e.g. "RED, FX6, MacBook".
          </DialogDescription>
        </DialogHeader>

        <div className="mb-2 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={addRow} className="gap-1">
            <Plus className="h-4 w-4" /> Add row
          </Button>
          <Button variant="outline" size="sm" onClick={downloadTemplate} className="gap-1">
            <Download className="h-4 w-4" /> Download template
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll}>
            Clear all
          </Button>
        </div>

        <div className="overflow-auto rounded-md border" style={{ maxHeight: "50vh" }}>
          <table data-bulk-grid className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur">
              <tr>
                <th className="w-8 border-b px-2 py-2" />
                {BULK_COLUMNS.map((col) => (
                  <th key={col} className="border-b px-2 py-2 text-left font-medium">
                    {COLUMN_LABELS[col]}
                  </th>
                ))}
                <th className="w-10 border-b px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => {
                // Hide the verdict for the row you're currently editing.
                const status = r === activeRow ? undefined : statusByRow.get(r);
                // Live gear preview for every row (independent of the
                // active-row message suppression), so parsed kit always
                // shows as pills right under the gear cell.
                const gearPreview = statusByRow.get(r)?.gear ?? [];
                return (
                  <tr key={r} className="group">
                    <td className="border-b px-1 pt-2.5 text-center align-top">
                      <RowStatusIcon status={status} />
                    </td>
                    {BULK_COLUMNS.map((col) => (
                      <td key={col} className="border-b p-0 align-top">
                        <Input
                          ref={(el) => {
                            if (el) cellRefs.current.set(`${r}-${col}`, el);
                            else cellRefs.current.delete(`${r}-${col}`);
                          }}
                          value={row[col]}
                          placeholder={r === 0 ? COLUMN_PLACEHOLDERS[col] : undefined}
                          list={col === "role" ? ROLE_DATALIST_ID : undefined}
                          onChange={(e) => setCell(r, col, e.target.value)}
                          onPaste={(e) => handlePaste(r, col, e)}
                          onKeyDown={(e) => handleKeyDown(r, col, e)}
                          onFocus={() => setActiveRow(r)}
                          onBlur={handleBlur}
                          className={cn(
                            "h-9 rounded-none border-0 bg-transparent focus-visible:ring-1 focus-visible:ring-inset",
                            status?.severity === "error" && col === "email" && !row.email.trim()
                              ? "ring-1 ring-inset ring-destructive/50"
                              : undefined,
                          )}
                        />
                        {col === "gear" && gearPreview.length > 0 && (
                          <div className="flex flex-wrap gap-1 px-2 pb-1.5">
                            {gearPreview.map((g, i) => (
                              <GearBadge key={i} gear={g} />
                            ))}
                          </div>
                        )}
                      </td>
                    ))}
                    <td className="border-b px-1 pt-2 text-center align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(r)}
                        className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                        aria-label="Remove row"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Role suggestions — nudges pasted/typed roles toward the
            canonical values that keep CAM ID assignment working. */}
        <datalist id={ROLE_DATALIST_ID}>
          {CREW_ROLES.map((role) => (
            <option key={role} value={role} />
          ))}
        </datalist>

        {/* Per-row messages: errors, warnings, duplicate resolution. The
            row being edited is hidden until you move off it. */}
        {validated.some((v) => v.index !== activeRow && (v.errors.length || v.warnings.length)) && (
          <div className="mt-2 max-h-28 space-y-1 overflow-auto text-xs">
            {validated.map((v) => {
              if (v.index === activeRow) return null;
              if (!v.errors.length && !v.warnings.length) return null;
              return (
                <div key={v.index} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-muted-foreground">Row {v.index + 1}:</span>
                  {v.errors.map((msg) => (
                    <span key={msg} className="text-destructive">
                      {msg}
                    </span>
                  ))}
                  {v.warnings.map((msg) => (
                    <span key={msg} className="text-amber-500">
                      {msg}
                    </span>
                  ))}
                  {v.duplicateOf && (
                    <span className="ml-1 inline-flex overflow-hidden rounded border">
                      <button
                        type="button"
                        onClick={() => setResolutions((p) => ({ ...p, [v.duplicateOf!]: "skip" }))}
                        className={cn(
                          "px-2 py-0.5",
                          (resolutions[v.duplicateOf] ?? "skip") === "skip"
                            ? "bg-muted font-medium"
                            : "text-muted-foreground",
                        )}
                      >
                        Skip
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setResolutions((p) => ({ ...p, [v.duplicateOf!]: "update" }))
                        }
                        className={cn(
                          "px-2 py-0.5",
                          (resolutions[v.duplicateOf] ?? "skip") === "update"
                            ? "bg-primary/15 font-medium text-primary"
                            : "text-muted-foreground",
                        )}
                      >
                        Update
                      </button>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-2 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="text-foreground">{summary.insert} to add</span>
            {summary.update > 0 && <span>{summary.update} to update</span>}
            {summary.skip > 0 && <span>{summary.skip} skipped</span>}
            {summary.error > 0 && (
              <span className="text-destructive">{summary.error} with errors</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button disabled={!canSave} onClick={() => onSubmit(buildSaveRows())}>
              {saving
                ? "Saving..."
                : `Add ${summary.insert + summary.update} member${
                    summary.insert + summary.update === 1 ? "" : "s"
                  }`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RowStatusIcon = ({ status }: { status?: ValidatedRow }) => {
  if (!status) return null;
  if (status.severity === "error")
    return <AlertCircle className="mx-auto h-4 w-4 text-destructive" />;
  if (status.severity === "warning") return <Info className="mx-auto h-4 w-4 text-amber-500" />;
  return <CheckCircle2 className="mx-auto h-4 w-4 text-primary" />;
};

export default BulkAddDialog;
